<template>
  <component :is="tag" v-bind="binding" v-on="$listeners" class="card-v2">
    <div class="card-v2__thumbnail" v-if="hasThumbnail">
      <slot name="thumbnail">
        <img :src="thumbnail" />
      </slot>
    </div>

    <header v-if="hasHeader" class="card-v2__header">
      <slot name="header">
        <div v-if="title" class="card-v2__title">{{ title }}</div>
        <slot name="subtitle">
          <div v-if="title && subtitle" class="card-v2__subtitle">{{ subtitle }}</div></slot
        >
      </slot>
    </header>
    <div v-if="hasContent" class="card-v2__content">
      <slot />
    </div>
    <div v-if="hasActions" class="card-v2__actions">
      <slot name="actions" />
    </div>
  </component>
</template>

<script>
export default {
  props: {
    tag: {
      type: String,
      default: 'div',
    },
    thumbnail: String,
    title: String,
    subtitle: String,
    variant: {
      type: String,
      enums: ['widget', 'prompt', 'default'],
      default: 'default',
    },
  },
  computed: {
    binding() {
      return {
        ...this.$attrs,
        class: ['card-v2', 'card-v2--' + this.variant],
      }
    },
    hasHeader() {
      return this.title || this.$slots.header
    },
    hasActions() {
      return this.$slots.actions
    },
    hasContent() {
      return this.$slots.default
    },
    hasThumbnail() {
      return this.thumbnail || this.$slots.thumbnail
    },
  },
  components: {},
}
</script>

<style lang="scss">
.card-v2 {
  display: grid;
  overflow: hidden;

  border-radius: var(--border-radius-md);
  background-color: white;
  text-decoration: none;
  color: inherit;
  box-shadow: var(--shadow-lg);

  // Replace box-shadow with border on nested
  .card-v2 {
    box-shadow: none;
    border: 2px solid var(--light-grey);

    .card-v2__title {
      @include typography('h3');
    }
  }

  &--default {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
    grid-template-areas: 'header' 'content' 'actions';

    &:has(.card-v2__thumbnail) {
      grid-template-areas: 'thumbnail' 'header' 'content' 'actions';
    }

    & > .card-v2__content {
      --card-padding: var(--spacing-lg);
      padding: var(--card-padding);
    }

    &:has(.card-v2__header) > .card-v2__content {
      --card-padding: var(--spacing-lg);
      padding: 0 var(--card-padding) var(--card-padding);
    }

    & > .card-v2__actions {
      padding: var(--spacing-lg);
      border-top: 2px solid var(--light-grey);

      display: flex;
      justify-content: center;
      align-items: center;
      gap: var(--spacing-default);
    }
  }

  &--prompt {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
    grid-template-areas: 'header' 'content' 'actions';

    &:has(.card-v2__thumbnail) {
      grid-template-areas: 'thumbnail' 'header' 'content' 'actions';
    }

    & > .card-v2__header {
      background-color: var(--lightest-grey);
    }

    & > .card-v2__content {
      --card-padding: var(--spacing-lg);
      padding: var(--card-padding);
    }

    & > .card-v2__actions {
      background-color: var(--lightest-grey);

      padding: var(--spacing-lg);

      display: flex;
      justify-content: center;
      align-items: center;
      gap: var(--spacing-default);
    }
  }

  &--widget {
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto 1fr;
    grid-template-areas: 'header actions' 'content content';

    &:has(.card-v2__thumbnail) {
      grid-template-areas: 'thumbnail thumbnail' 'header actions' 'content content';
    }

    & > .card-v2__content {
      --card-padding: var(--spacing-lg);
      padding: var(--card-padding);
      border-top: 2px solid var(--lightest-grey);
    }

    & > .card-v2__actions {
      padding: var(--spacing-lg);

      display: flex;
      justify-content: center;
      align-items: center;
      gap: var(--spacing-default);
    }
  }

  &__thumbnail {
    grid-area: thumbnail;
    aspect-ratio: 16 / 9;
    width: 100%;
  }

  & > &__header {
    grid-area: header;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: var(--spacing-lg);
  }

  & > &__content {
    grid-area: content;
  }

  & > &__actions {
    grid-area: actions;
  }

  &__title {
    margin-block-end: 0;

    @include typography('h2');
  }

  &__subtitle {
    margin-block: 0;

    @include typography('body');
  }
}
</style>
